import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  MdHourglassEmpty,
  MdReceiptLong,
  MdCheckCircle,
  MdEdit,
} from "react-icons/md";
import { toast } from "react-toastify";
import { Billing, BillingTypeName } from "../../../../interfaces/Financial";
import SiimpStatus from "./SiimpStatus";

interface FinancialKanbanProps {
  billings: Billing[];
  onEditServiceOrder: (billing: Billing) => void;
  onEmitInvoice: (billingId: number) => Promise<void>;
  /** Recarrega a lista (usado pelo "Reprocessar" do SIIMP). */
  onRefresh: () => void;
  onMarkReceived: (billingId: number) => Promise<void>;
}

const fmtBRL = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";

type Accent = "amber" | "blue" | "cyan" | "emerald";

const PHASES: { phase: number; title: string; icon: React.ReactNode; accent: Accent }[] = [
  { phase: 1, title: "Pendente de emissão de NF", icon: <MdHourglassEmpty className="h-5 w-5" />, accent: "amber" },
  { phase: 2, title: "Faturada", icon: <MdReceiptLong className="h-5 w-5" />, accent: "blue" },
  { phase: 3, title: "Recebida", icon: <MdCheckCircle className="h-5 w-5" />, accent: "emerald" },
];

const accentMap: Record<Accent, { header: string; chip: string; rail: string; btn: string }> = {
  amber: {
    header: "text-amber-600 dark:text-amber-400",
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    rail: "before:bg-amber-400",
    btn: "bg-gradient-to-r from-amber-500 to-orange-500 shadow-amber-500/25",
  },
  blue: {
    header: "text-dv-blue-600 dark:text-dv-blue-300",
    chip: "bg-dv-blue-50 text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-blue-300",
    rail: "before:bg-dv-blue-500",
    btn: "bg-gradient-to-r from-dv-blue-500 to-dv-blue-600 shadow-dv-blue-500/25",
  },
  cyan: {
    header: "text-dv-cyan-600 dark:text-dv-cyan-300",
    chip: "bg-dv-cyan-50 text-dv-cyan-700 dark:bg-dv-cyan-900/40 dark:text-dv-cyan-300",
    rail: "before:bg-dv-cyan-500",
    btn: "bg-gradient-to-r from-dv-cyan-500 to-dv-blue-500 shadow-dv-cyan-500/25",
  },
  emerald: {
    header: "text-emerald-600 dark:text-emerald-400",
    chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    rail: "before:bg-emerald-400",
    btn: "",
  },
};

const FinancialKanban: React.FC<FinancialKanbanProps> = ({
  billings,
  onEditServiceOrder,
  onEmitInvoice,
  onRefresh,
  onMarkReceived,
}) => {
  const [filterClientId, setFilterClientId] = useState<number | "">("");
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);

  const clients = useMemo(() => {
    const map = new Map<number, string>();
    billings.forEach((b) => map.set(b.clientId, b.clientName));
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [billings]);

  const visible = useMemo(
    () =>
      billings
        .filter((b) => b.phase >= 1 && b.phase <= 3)
        .filter((b) => filterClientId === "" || b.clientId === filterClientId),
    [billings, filterClientId]
  );

  const handleAction = async (fn: (id: number) => Promise<void>, id: number) => {
    setActionLoadingId(id);
    try {
      await fn(id);
    } finally {
      setActionLoadingId(null);
    }
  };

  // Avançar uma fase via drag dispara a ação correspondente. Voltar/pular fase é ignorado.
  // Fase 1 (pendente NF) → Emitir NF → fase 2 (faturada) → Marcar recebido → fase 3 (recebida).
  const phaseAction: Record<number, (id: number) => Promise<void>> = {
    1: onEmitInvoice,
    2: onMarkReceived,
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    const from = Number(source.droppableId);
    const to = Number(destination.droppableId);
    if (to === from) return; // soltou na mesma coluna — nada a fazer
    // Só avança exatamente uma fase; explica o motivo em vez de ignorar em silêncio.
    if (to < from) {
      toast.info("Não é possível voltar uma cobrança para a fase anterior. Use Cancelar se o lançamento estiver errado.");
      return;
    }
    if (to > from + 1) {
      toast.info("Avance uma fase por vez: primeiro emita a NF, depois marque como recebida.");
      return;
    }
    const action = phaseAction[from];
    if (action) handleAction(action, Number(draggableId));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="kanban-client" className="text-xs font-semibold text-gray-700 dark:text-gray-300">
            Cliente
          </label>
          <select
            id="kanban-client"
            value={filterClientId}
            onChange={(e) => setFilterClientId(e.target.value === "" ? "" : Number(e.target.value))}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          >
            <option value="">Todos os clientes</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {PHASES.map(({ phase, title, icon, accent }) => {
          const items = visible.filter((b) => b.phase === phase);
          const total = items.reduce((s, b) => s + b.amount, 0);
          const cls = accentMap[accent];
          return (
            <div key={phase} className="flex flex-col rounded-2xl bg-gray-50 p-3 dark:bg-navy-800/50">
              <div className="mb-1 flex items-center justify-between">
                <div className={`flex items-center gap-2 text-sm font-bold ${cls.header}`}>
                  {icon}
                  <span className="leading-tight">{title}</span>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${cls.chip}`}>{items.length}</span>
              </div>
              <p className="mb-3 text-xs font-semibold tabular-nums text-gray-700 dark:text-gray-300">{fmtBRL(total)}</p>

              <Droppable droppableId={String(phase)}>
                {(dropProvided, dropSnapshot) => (
                  <div
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                    className={`flex max-h-[64vh] min-h-[60px] flex-col gap-2 overflow-y-auto rounded-xl pr-1 transition-colors duration-200 ${
                      dropSnapshot.isDraggingOver ? "ring-2 ring-dv-blue-400" : ""
                    }`}
                  >
                    {items.length === 0 && !dropSnapshot.isDraggingOver && (
                      <p className="px-2 py-6 text-center text-xs italic text-gray-500 dark:text-gray-400">Nenhuma cobrança.</p>
                    )}
                    {items.map((b, index) => (
                      <Draggable draggableId={String(b.billingId)} index={index} key={b.billingId}>
                        {(dragProvided, dragSnapshot) => {
                          const card = (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            style={{
                              ...dragProvided.draggableProps.style,
                              ...(dragSnapshot.isDragging
                                ? { transform: `${dragProvided.draggableProps.style?.transform ?? ""} rotate(3deg)` }
                                : {}),
                            }}
                            className={`relative cursor-grab overflow-hidden rounded-xl border border-gray-100 bg-white p-3 active:cursor-grabbing dark:border-white/10 dark:bg-navy-900
                        before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:content-[''] ${cls.rail} ${
                              dragSnapshot.isDragging ? "shadow-dv-lg ring-2 ring-dv-blue-400" : "shadow-dv-sm"
                            }`}
                          >
                      <div className="flex items-start justify-between gap-2">
                        <span className="truncate text-sm font-bold text-navy-700 dark:text-white" title={b.clientName}>
                          {b.clientName}
                        </span>
                        <span className="shrink-0 text-sm font-extrabold tabular-nums text-navy-700 dark:text-white">
                          {fmtBRL(b.amount)}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-gray-700 dark:text-gray-300">
                        {b.projectName || `OS #${b.billingId}`}
                      </div>
                      <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                        {BillingTypeName[b.billingType]} · {String(b.monthRef).padStart(2, "0")}/{b.yearRef}
                        {b.invoiceNumber && <> · NF {b.invoiceNumber}</>}
                      </div>

                      <SiimpStatus billing={b} onChange={onRefresh} />

                      {/* Datas do ciclo */}
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                        {b.dtCharged && <span>Faturado {fmtDate(b.dtCharged)}</span>}
                        {b.dtEmitted && <span>NF {fmtDate(b.dtEmitted)}</span>}
                      </div>

                      {/* Ações */}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {phase === 1 && (
                          <>
                            {b.status === 5 && (
                              <button
                                onClick={() => onEditServiceOrder(b)}
                                className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
                              >
                                <MdEdit className="h-3.5 w-3.5" aria-hidden="true" /> Editar
                              </button>
                            )}
                            {!b.siimpEtapa && !b.siimpErro && (
                              <PhaseButton cls={cls.btn} loading={actionLoadingId === b.billingId} onClick={() => handleAction(onEmitInvoice, b.billingId)}>
                                Emitir NF
                              </PhaseButton>
                            )}
                          </>
                        )}
                        {phase === 2 && (
                          <PhaseButton cls={cls.btn} loading={actionLoadingId === b.billingId} onClick={() => handleAction(onMarkReceived, b.billingId)}>
                            Marcar recebido
                          </PhaseButton>
                        )}
                        {phase === 3 && (
                          <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            <MdCheckCircle className="h-3.5 w-3.5" aria-hidden="true" /> Recebida
                          </span>
                        )}
                      </div>
                          </div>
                          );
                          // Durante o arraste, portamos o card no <body> para escapar de
                          // ancestrais com `transform` (ex.: animate-fade-up) que quebram o
                          // posicionamento `position: fixed` do @hello-pangea/dnd.
                          return dragSnapshot.isDragging
                            ? createPortal(card, document.body)
                            : card;
                        }}
                      </Draggable>
                    ))}
                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
      </DragDropContext>
    </div>
  );
};

const PhaseButton: React.FC<{
  cls: string;
  loading: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ cls, loading, onClick, children }) => (
  <button
    disabled={loading}
    onClick={onClick}
    className={`rounded-lg px-3 py-1 text-[11px] font-bold text-white shadow-md transition-all hover:brightness-110 active:scale-[.97] disabled:opacity-50 ${cls}`}
  >
    {loading ? "…" : children}
  </button>
);

export default FinancialKanban;
